import React from "react";
import {useMutation} from "@tanstack/react-query";
import {API} from "api";
import {track} from "libs/analytics";
import {ALL_ROLES, mayChangeFlagTarget, type UserRole} from "data/permissions";
import {useAuth} from "hooks/useAuth";
import {
  FEATURE_AREAS,
  FEATURES,
  featureAllowedFor,
  resolveFeature,
  type FeatureDef,
  type FeatureTargetsDoc,
} from "data/features";

const ROLE_LABELS: Record<UserRole, string> = {
  superadmin: "Superadmin",
  admin: "Admin",
  internal: "Internal",
  designer: "Designer",
  partner: "Partner",
  external: "External",
};

interface Props {
  docs: readonly FeatureTargetsDoc[];
  onChanged: () => void;
  onError: (message: string) => void;
}

/**
 * The registered features (data/features.ts) × every role. A checkbox shows
 * what the role has now: the admin target, else the default. A role that
 * cannot have the feature (it lacks the capability of the control's API) has
 * a disabled checkbox. So has the superadmin column for an admin: only a
 * superadmin changes the superadmin target.
 */
export default function RoleFeatures({docs, onChanged, onError}: Props) {
  const {role: callerRole} = useAuth() as {role: string | null};
  const byKey = new Map(docs.map((doc) => [doc.key, doc]));
  const update = useMutation({
    mutationFn: ({key, role, enabled}: {key: string; role: UserRole; enabled: boolean}) =>
      API.updateFeatureFlag(key, role, {enabled}),
    onSuccess: onChanged,
    onError: () => onError("Failed to update the feature"),
  });
  const reset = useMutation({
    mutationFn: (key: string) => API.deleteFeatureFlag(key),
    onSuccess: onChanged,
    onError: () => onError("Failed to reset the feature"),
  });
  const busy = update.isPending || reset.isPending;

  const toggle = (def: FeatureDef, role: UserRole, enabled: boolean) => {
    track("neurobaker.admin.flag_changed", {action: "update", key: def.key, role, enabled});
    update.mutate({key: def.key, role, enabled});
  };
  const resetFeature = (def: FeatureDef) => {
    track("neurobaker.admin.flag_changed", {action: "reset", key: def.key});
    reset.mutate(def.key);
  };

  return (
    <section className="admin-flags-role-features" data-test="role-features">
      <div>
        <h2 className="admin-flags-section-title">Role features</h2>
        <p className="admin-flags-section-text">
          Editor controls per role. A feature only shows or hides a control: an API keeps its own
          permission. A dot marks a role that differs from the default. Each environment has its
          own settings.
        </p>
      </div>
      {FEATURE_AREAS.map((area) => {
        const features = (FEATURES as readonly FeatureDef[]).filter((f) => f.area === area);
        if (features.length === 0) return null;
        return (
          <table key={area} className="admin-flags-role-table">
            <thead>
              <tr>
                <th className="admin-flags-role-table__feature">{area}</th>
                {ALL_ROLES.map((role) => (
                  <th key={role}>{ROLE_LABELS[role]}</th>
                ))}
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {features.map((def) => {
                const doc = byKey.get(def.key);
                const changed = ALL_ROLES.some((role) => typeof doc?.targets?.[role] === "boolean");
                return (
                  <tr key={def.key} data-feature={def.key}>
                    <td className="admin-flags-role-table__feature">
                      <div className="admin-flags-role-table__name">{def.name}</div>
                      <div className="admin-flags-role-table__key">{def.key}</div>
                      <div className="admin-flags-role-table__description">{def.description}</div>
                    </td>
                    {ALL_ROLES.map((role) => {
                      const allowed = featureAllowedFor(def, role);
                      const mayChange = mayChangeFlagTarget(callerRole, role);
                      const on = resolveFeature(def, role, doc);
                      const isDefault = def.defaultRoles.includes(role);
                      const differs = allowed && on !== isDefault;
                      return (
                        <td key={role} className="admin-flags-role-table__cell">
                          <label
                            title={
                              !allowed
                                ? `${ROLE_LABELS[role]} cannot have it: needs ${def.requires}`
                                : `${ROLE_LABELS[role]}: ${on ? "on" : "off"} (default ${isDefault ? "on" : "off"})` +
                                  (mayChange ? "" : ". Only a superadmin can change it.")
                            }
                          >
                            <input
                              type="checkbox"
                              data-role={role}
                              checked={on}
                              disabled={!allowed || !mayChange || busy}
                              onChange={(e) => toggle(def, role, e.target.checked)}
                            />
                            {differs && <span className="admin-flags-role-table__changed" />}
                          </label>
                        </td>
                      );
                    })}
                    <td className="admin-flags-role-table__actions">
                      {changed && (
                        <button
                          type="button"
                          className="admin-flags-role-table__reset"
                          disabled={busy}
                          title={
                            mayChangeFlagTarget(callerRole, "superadmin")
                              ? "Every role gets the default again"
                              : "Every role but Superadmin gets the default again"
                          }
                          onClick={() => resetFeature(def)}
                        >
                          Reset
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      })}
    </section>
  );
}
